import { initializeApp, getApps } from "firebase/app";
import { getAnalytics, isSupported, logEvent, type Analytics } from "firebase/analytics";
import { getFirebaseApp } from "./index.js";

const initialized = new Set<string>();
let activeAnalytics: Analytics | undefined;

/**
 * Initialize Firebase Analytics for a client site.
 *
 * The Firebase Analytics SDK resolves the *authoritative* measurementId for a
 * given Firebase App ID from Firebase's own servers at init time — it does NOT
 * simply trust whatever measurementId is passed into initializeApp() locally.
 * That means each client needs its own registered Firebase Web App (its own
 * appId, provisioned by `g8 client promote`) to report to its own GA4 stream;
 * reusing the platform's shared appId with a different measurementId string
 * silently redirects every client's traffic to the same stream and sends nothing
 * for the "orphaned" GA4 streams created directly via the Admin API.
 *
 * Safe to call multiple times — no-ops if already initialized for this ID.
 */
export async function initClientAnalytics(measurementId: string, appId?: string): Promise<void> {
  if (!measurementId || initialized.has(measurementId)) return;
  if (!(await isSupported())) return;

  const baseApp = getFirebaseApp();

  if (!appId) {
    console.warn(
      `[analytics] no Firebase appId configured for measurementId ${measurementId} — ` +
        "falling back to the shared platform app, so this client's traffic will be mixed " +
        "into that app's own stream. Run `g8 client promote` to provision a dedicated appId.",
    );
    activeAnalytics = getAnalytics(baseApp);
  } else if (appId === baseApp.options.appId) {
    activeAnalytics = getAnalytics(baseApp);
  } else {
    const appName = `analytics-${appId}`;
    const app =
      getApps().find((a) => a.name === appName) ??
      initializeApp({ ...baseApp.options, appId, measurementId }, appName);
    activeAnalytics = getAnalytics(app);
  }

  initialized.add(measurementId);
}

/**
 * Log a custom GA4 event (typically a conversion — quote submitted, reservation booked,
 * newsletter signup, etc.) for whichever client site called initClientAnalytics/
 * initAdminAnalytics in this session. No-ops silently if analytics hasn't initialized yet
 * (e.g. the config hadn't loaded when the event fired) — conversions are best-effort
 * telemetry, not something that should ever block or throw on the calling business logic.
 */
export function logConversionEvent(eventName: string, params?: Record<string, string | number | boolean>): void {
  if (!activeAnalytics) return;
  logEvent(activeAnalytics, eventName, params);
}

/**
 * Initialize Firebase Analytics for the admin portal using VITE_FIREBASE_MEASUREMENT_ID.
 * Call once at app boot (main.tsx). No-ops if the env var is not set.
 */
export async function initAdminAnalytics(): Promise<void> {
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined;
  if (measurementId) await initClientAnalytics(measurementId);
}
